import appContext from 'legacy/appcontext';
import moment from 'moment-wrapper';
import pickBy from 'lodash.pickby';
import objectRemoveMinAttribute from 'utils/object/objectRemoveMinAttribute';

const STORAGE_KEY = 'app-cache';

// in common case it is half size of localStorage (avg capacity of localStorage is 5BM)
const MAX_STORAGE_SYMBOLS = 5 * 1024 * 1024 / 2 / 2;

class CacheStorage {
    constructor (maxStorageSymbols) {
        this.maxStorageSymbols = maxStorageSymbols;
    }

    getStorageInitialState () {
        return {
            version: undefined,
            store: {}
        };
    }

    loadStorage () {
        return appContext.getStorageValue(STORAGE_KEY) || this.getStorageInitialState();
    }

    updateStorage (val) {
        return appContext.setStorageValue(STORAGE_KEY, val);
    }

    clearOldData () {
        const storage = this.loadStorage();
        if (!storage.store) {
            return;
        }
        const currentDate = moment().toISOString();
        storage.store = pickBy(storage.store, (value) => value.dueDate >= currentDate);
        this.updateStorage(storage);
    }

    clearExtraData (rawStorageData) {
        let limitedData = rawStorageData;
        while (JSON.stringify(limitedData).length > this.maxStorageSymbols) {
            limitedData = objectRemoveMinAttribute(limitedData, 'dueDate');
        }
        return limitedData;
    }

    get storage () {
        this.clearOldData();
        const storage = this.loadStorage();
        return storage.store || {};
    }

    set storage (val) {
        const storage = this.loadStorage();
        storage.store = this.clearExtraData(val);

        try {
            this.updateStorage(storage);
        } catch (e) {
            // remove all from cache if storage is overflowed (in common case  clear if localStorage size > 5MB)
            if (e.name === 'QuotaExceededError') {
                console.warn('LocalStorage quota exceeded. Removing cache...');
                this.flushCache();
            } else {
                throw e;
            }
        }
    }

    setVersion (version) {
        const storage = this.loadStorage();
        storage.version = version;
        this.updateStorage(storage);
    }

    setStorageValue (key, val) {
        this.storage = {...this.storage, [key]: val};
    }

    getStorageVersion () {
        return this.loadStorage().version;
    }

    flushCache () {
        this.storage = {};
    }

    wrapStorageContainer (data, params) {
        const dueDate = moment().add(params.ttl, 'minutes').
            toISOString();
        return {
            dueDate,
            data
        };
    }

    unwrapStorageContainer (container) {
        if (container.dueDate && new Date(container.dueDate) > new Date()) {
            return container.data;
        }
        return null;
    }

    get (key) {

        const dataItem = this.storage[key];
        if (dataItem) {
            return this.unwrapStorageContainer(dataItem);
        }
        return null;
    }

    /**
     * Saves value to cacheStorage
     * @param key - store key name
     * @param value - value to store
     * @param params - {
     *   ttl - time to live for cached value
     * }
     */
    set (key, value, params) {
        this.setStorageValue(key, this.wrapStorageContainer(value, params));
    }
}

export default new CacheStorage(MAX_STORAGE_SYMBOLS);
export {CacheStorage};
